<template>
	<div>
		<form @submit.prevent="save()">
			<div class="gh-layout">
				<aside class="gh-intro">
					<h2 class="gh-intro__title">Short Links</h2>
					<p class="gh-intro__desc">
						Postcards print a short link (<code>{{ examplePrinted }}</code>) and a QR code that opens the same link with a trailing
						<code>q</code>. The <strong>shortlink-edge</strong> Cloudflare Worker answers on the short domains below, asks MC2 to
						record the visit and redirects to the campaign's destination URL with UTM params. Each campaign picks which domain it prints.
					</p>
				</aside>

				<section class="gh-accounts">
					<div v-if="loading" class="ada-card text-muted"><i class="fas fa-spinner fa-spin me-1"></i> Loading…</div>

					<template v-else>
						<div class="ada-card mb-3">
							<div class="gh-section__title"><i class="fas fa-link me-1"></i> Short domains</div>
							<div class="gh-help mb-2">
								Hosts the Worker answers on (Custom Domains of the Worker, or its <code>workers.dev</code> URL while testing). Campaigns
								without a choice print the <strong>default</strong> one. <strong>Printed links keep their host</strong>, so a domain must keep
								pointing at the Worker as long as postcards with it are out there.
							</div>
							<div v-for="(domain, index) in form.domains" :key="domain.id || `new-${index}`" class="sl-domain">
								<div class="sl-domain__grid">
									<div class="gh-field mb-0">
										<label :for="`domainLabel-${index}`">Name</label>
										<input :id="`domainLabel-${index}`" type="text" class="form-control" v-model.trim="domain.label" placeholder="TownWeb" required />
									</div>
									<div class="gh-field mb-0">
										<label :for="`domainHost-${index}`">Host</label>
										<input :id="`domainHost-${index}`" type="url" class="form-control" v-model.trim="domain.host" placeholder="https://tweb.app" required />
									</div>
									<div class="gh-field mb-0">
										<label :for="`domainFallback-${index}`">Fallback</label>
										<input :id="`domainFallback-${index}`" type="url" class="form-control" v-model.trim="domain.fallback_url" placeholder="https://townweb.com" />
									</div>
								</div>
								<div class="sl-domain__actions">
									<label class="sl-domain__default">
										<input type="radio" name="defaultShortDomain" :checked="domain.is_default" @change="setDefaultDomain(index)" /> Default
									</label>
									<button type="button" class="sl-icon-btn" title="Remove domain" aria-label="Remove domain" @click="form.domains.splice(index, 1)">
										<i class="fas fa-trash-alt"></i>
									</button>
								</div>
							</div>
							<div v-if="!form.domains.length" class="gh-help mb-2">No short domains — no short links (and no QR codes) are generated.</div>
							<button type="button" class="gh-save-btn gh-save-btn--sm" @click="addDomain"><i class="fas fa-plus"></i> Add domain</button>
							<div class="gh-help mt-2">
								A domain's <strong>Fallback</strong> is where its campaigns send visitors when they have no destination URL. The bare host and
								unknown codes are redirected by the Worker itself (<code>FALLBACK_URLS</code> in <code>wrangler.jsonc</code>).
							</div>
							<div class="gh-field mt-3">
								<label for="shortlinkFallbackUrl">Default fallback destination</label>
								<input id="shortlinkFallbackUrl" type="url" class="form-control" v-model.trim="form.fallback_url" placeholder="https://townweb.com" />
								<div class="gh-help">Used when neither the campaign nor its domain has a destination.</div>
							</div>
						</div>

						<div class="ada-card mb-3">
							<div class="gh-section__title"><i class="fas fa-key me-1"></i> Worker secret</div>
							<div class="gh-help mb-2">
								The Worker sends it in the <code>X-Shortlink-Secret</code> header to <code>{{ hookUrl }}</code>; MC2 rejects calls without it.
							</div>
							<div class="sl-secret">
								<span v-if="secretSet" class="sl-secret__status sl-secret__status--ok"><i class="fas fa-check-circle"></i> Set ({{ secretHint }})</span>
								<span v-else class="sl-secret__status"><i class="fas fa-exclamation-circle"></i> Not set — the Worker cannot record visits</span>
								<button type="button" class="gh-save-btn gh-save-btn--sm" :disabled="saving" @click="$refs.generateModal.open()">
									<i class="fas fa-sync"></i> {{ secretSet ? 'Generate new secret' : 'Generate secret' }}
								</button>
							</div>

							<div v-if="newSecret" class="sl-new-secret">
								<div class="sl-new-secret__head">
									<strong>New secret — copy it now, it is not shown again.</strong>
									<button type="button" class="gh-save-btn gh-save-btn--sm" @click="copy(newSecret)"><i class="fas fa-copy"></i> Copy</button>
								</div>
								<code class="sl-new-secret__value">{{ newSecret }}</code>
								<div class="gh-help mt-2">
									Set it on the Worker (no redeploy needed):
									<code>npx wrangler secret put SHORTLINK_SECRET</code> (run in <code>shortlink-edge</code>, paste the value). Until then the
									Worker keeps redirecting but visits are not recorded.
								</div>
							</div>

							<div class="gh-field mt-3">
								<label for="shortlinkSecret">Or set a secret manually</label>
								<input
									id="shortlinkSecret"
									type="password"
									class="form-control"
									v-model="form.secret"
									autocomplete="new-password"
									placeholder="Leave empty to keep the current secret"
								/>
								<div class="gh-help">At least 24 characters; must equal the Worker's <code>SHORTLINK_SECRET</code>.</div>
								</div>
								</div>

								<div class="ada-card mb-3">
									<div class="gh-section__title"><i class="fab fa-slack me-1"></i> Slack notifications</div>
									<div class="gh-help mb-2">
										Campaigns with <strong>Slack notification on visit</strong> turned on post a message here for every short-link visit
										(bots and test links excluded). Create an <em>Incoming Webhook</em> in the Slack app for the channel you want and paste its URL.
									</div>
									<div class="sl-secret">
										<span v-if="slackSet" class="sl-secret__status sl-secret__status--ok"><i class="fas fa-check-circle"></i> Webhook set ({{ slackHint }})</span>
										<span v-else class="sl-secret__status"><i class="fas fa-exclamation-circle"></i> No webhook — visit messages are off</span>
										<div class="d-flex gap-2">
											<button v-if="slackSet" type="button" class="gh-save-btn gh-save-btn--sm" :disabled="slackTesting" @click="sendSlackTest">
												<i class="fas" :class="slackTesting ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i> Send test message
											</button>
											<button v-if="slackSet" type="button" class="gh-save-btn gh-save-btn--sm gh-save-btn--danger" :disabled="saving" @click="save({ clear_slack_webhook: true })">
												<i class="fas fa-times"></i> Remove
											</button>
										</div>
									</div>
									<div class="gh-field mt-3">
										<label for="shortlinkSlackWebhook">{{ slackSet ? 'Replace webhook URL' : 'Webhook URL' }}</label>
										<input
											id="shortlinkSlackWebhook"
											type="password"
											class="form-control"
											v-model.trim="form.slack_webhook_url"
											autocomplete="off"
											placeholder="https://hooks.slack.com/services/…"
										/>
										<div class="gh-help">Saved with "Save changes". The URL is a secret and is never shown again in full.</div>
									</div>
								</div>
					</template>
				</section>
			</div>

			<hr class="gh-divider" />

			<div class="gh-save-row">
				<button class="gh-save-btn" type="submit" :disabled="saving || loading">
					<i class="fas" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
					Save changes
				</button>
			</div>

			<ConfirmModal
				ref="generateModal"
				title="Generate a new secret?"
				confirm-text="Generate"
				confirm-icon="fa-key"
				icon-class="fa-key"
				:busy="saving"
				@confirm="generateSecret"
			>
				The Worker stops recording visits until it gets the new secret (redirects keep working).
			</ConfirmModal>
		</form>

		<div class="gh-layout mt-4">
			<aside class="gh-intro">
				<h2 class="gh-intro__title">Test short links</h2>
				<p class="gh-intro__desc">
					Create a real short link without a campaign to check the Worker end to end: open it or scan its QR code with a phone,
					then refresh to see the visit. Empty UTM fields get the postcard defaults (<code>utm_source=postcard</code>,
					<code>utm_medium=direct_mail</code>); QR scans get <code>_qr</code> appended to <code>utm_content</code> (or just <code>qr</code>).
					Quick repeat visits from the same visitor are shown but not counted.
				</p>
			</aside>

			<section class="gh-accounts">
				<form class="ada-card mb-3" @submit.prevent="createTest">
					<div class="gh-section__title"><i class="fas fa-vial me-1"></i> New test link</div>
					<div class="gh-field">
						<label for="testDestination">Redirect to</label>
						<input id="testDestination" type="url" class="form-control" v-model.trim="test.destination" placeholder="https://clerkminutes.com/trial" required />
					</div>
					<div v-if="savedDomains.length > 1" class="gh-field">
						<label for="testDomain">Short domain</label>
						<select id="testDomain" class="form-select" v-model="test.shortlink_domain">
							<option :value="null">Default ({{ savedDomains[0].label }})</option>
							<option v-for="domain in savedDomains" :key="domain.id" :value="domain.id">{{ domain.label }} — {{ domain.host }}</option>
						</select>
					</div>
					<div class="sl-utm-grid">
						<div v-for="field in utmFields" :key="field.key" class="gh-field">
							<label :for="`test-${field.key}`">{{ field.key }}</label>
							<input :id="`test-${field.key}`" type="text" class="form-control" v-model.trim="test[field.key]" :placeholder="field.placeholder" />
						</div>
					</div>
					<div class="d-flex justify-content-end">
						<button type="submit" class="gh-save-btn gh-save-btn--sm" :disabled="creatingTest || !savedDomains.length">
							<i class="fas" :class="creatingTest ? 'fa-spinner fa-spin' : 'fa-plus'"></i> Generate test link
						</button>
					</div>
					<div v-if="!savedDomains.length" class="gh-help mt-2">Add and save a short domain first.</div>
				</form>

				<div class="ada-card">
					<div class="sl-tests-head">
						<div class="gh-section__title mb-0"><i class="fas fa-list me-1"></i> Test links</div>
						<button type="button" class="gh-save-btn gh-save-btn--sm" :disabled="loadingTests" @click="loadTests">
							<i class="fas fa-sync" :class="{ 'fa-spin': loadingTests }"></i> Refresh
						</button>
					</div>

					<div v-if="!tests.length" class="gh-help mt-2">{{ loadingTests ? 'Loading…' : 'No test links yet.' }}</div>

					<div v-for="item in tests" :key="item.id" class="sl-test">
						<div class="sl-test__qr" v-html="item.qr_svg" :title="item.qr_url"></div>
						<div class="sl-test__body">
							<div class="sl-test__row">
								<span class="sl-test__label">Typed</span>
								<code>{{ item.short_url }}</code>
								<button type="button" class="sl-icon-btn" title="Copy" aria-label="Copy typed link" @click="copy(item.short_url)"><i class="fas fa-copy"></i></button>
							</div>
							<div class="sl-test__row">
								<span class="sl-test__label">QR</span>
								<code>{{ item.qr_url }}</code>
								<button type="button" class="sl-icon-btn" title="Copy" aria-label="Copy QR link" @click="copy(item.qr_url)"><i class="fas fa-copy"></i></button>
							</div>
							<div class="sl-test__row sl-test__row--muted">
								<span class="sl-test__label">Lands on</span>
								<span class="sl-test__redirect" :title="item.redirect_url">{{ item.redirect_url }}</span>
							</div>
							<div class="sl-test__row sl-test__row--muted">
								<span class="sl-test__label">QR lands</span>
								<span class="sl-test__redirect" :title="item.redirect_qr_url">{{ item.redirect_qr_url }}</span>
							</div>
							<div class="sl-test__meta">
								<button
									v-if="item.visits"
									type="button"
									class="sl-link-btn"
									:aria-expanded="String(openTimelineId === item.id)"
									@click="openTimelineId = openTimelineId === item.id ? null : item.id"
								>
									<i class="fas" :class="openTimelineId === item.id ? 'fa-chevron-up' : 'fa-stream'"></i> Timeline
								</button>
								<strong>{{ item.visits }}</strong> {{ item.visits === 1 ? 'visit' : 'visits' }}
								<template v-if="item.last_visited_at">
									· last {{ formatDate(item.last_visited_at) }}
									<template v-if="item.last_channel">· {{ item.last_channel === 'qr' ? 'QR scan' : 'typed' }}</template>
									<template v-if="item.last_location">· {{ item.last_location }}</template>
								</template>
							</div>
						</div>
						<div v-if="openTimelineId === item.id" class="sl-test__timeline">
							<ShortlinkVisitTimeline type="test" :id="item.id" />
						</div>
						<div class="sl-test__actions">
							<a :href="item.short_url" target="_blank" rel="noopener" class="gh-save-btn gh-save-btn--sm" title="Opens the link and counts a visit">
								<i class="fas fa-external-link-alt"></i> Open
							</a>
							<button type="button" class="sl-icon-btn" title="Delete" aria-label="Delete test link" @click="deleteTest(item)"><i class="fas fa-trash-alt"></i></button>
						</div>
					</div>
				</div>
			</section>
		</div>
	</div>
</template>

<script>
import restApi from '@/api/index'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import ShortlinkVisitTimeline from '@/components/campaign/ShortlinkVisitTimeline.vue'
import { formatUtc } from '@/utils/time'

const EMPTY_TEST = { destination: '', shortlink_domain: null, utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '' }

export default {
	name: 'ShortlinkSettings',
	components: { ConfirmModal, ShortlinkVisitTimeline },
	data() {
		return {
			loading: true,
			saving: false,
			form: { domains: [], fallback_url: '', secret: '', slack_webhook_url: '' },
			savedDomains: [],
			secretSet: false,
			secretHint: null,
			hookUrl: '',
			newSecret: null,
			slackSet: false,
			slackHint: null,
			slackTesting: false,
			test: { ...EMPTY_TEST },
			tests: [],
			loadingTests: false,
			creatingTest: false,
			openTimelineId: null,
			utmFields: [
				{ key: 'utm_source', placeholder: 'postcard' },
				{ key: 'utm_medium', placeholder: 'direct_mail' },
				{ key: 'utm_campaign', placeholder: 'e.g. cm_fall_test' },
				{ key: 'utm_content', placeholder: 'e.g. front_v2 (+ _qr on scans)' },
			],
		}
	},
	computed: {
		examplePrinted() {
			const fallback = this.form.domains.find(domain => domain.is_default) || this.form.domains[0]
			const host = ((fallback && fallback.host) || 'https://twn.link').replace(/^https?:\/\//, '').replace(/\/+$/, '')
			return `${host}/a7k2m`
		},
	},
	created() {
		this.load()
		this.loadTests()
	},
	methods: {
		apply(data) {
			const domains = (data.domains || []).map(domain => ({ ...domain, fallback_url: domain.fallback_url || '' }))
			this.form = { domains, fallback_url: data.fallback_url || '', secret: '', slack_webhook_url: '' }
			// Saved domains only: test links can only use a domain MC2 already knows.
			this.savedDomains = domains.map(domain => ({ ...domain }))
			this.slackSet = data.slack_webhook_set
			this.slackHint = data.slack_webhook_hint
			this.secretSet = data.secret_set
			this.secretHint = data.secret_hint
			this.hookUrl = data.hook_url
		},
		async load() {
			this.loading = true
			try {
				const { data } = await restApi.get('/shortlink-settings')
				this.apply(data)
			} catch (error) {
				this.showError(error)
			} finally {
				this.loading = false
			}
		},
		addDomain() {
			this.form.domains.push({ id: null, label: '', host: '', fallback_url: '', is_default: this.form.domains.length === 0 })
		},
		setDefaultDomain(index) {
			this.form.domains = this.form.domains.map((domain, i) => ({ ...domain, is_default: i === index }))
		},
		async save(extra = {}) {
			this.saving = true
			try {
				const { data } = await restApi.put('/shortlink-settings', { ...this.form, ...extra })
				this.apply(data)
				this.newSecret = data.new_secret || null
				this.$swal.fire({ icon: 'success', title: 'Short link settings saved', timer: 2500, showConfirmButton: false })
				return true
			} catch (error) {
				this.showError(error)
				return false
			} finally {
				this.saving = false
			}
		},
		async generateSecret() {
			const saved = await this.save({ generate_secret: true })
			if (saved) this.$refs.generateModal.close()
		},
		async sendSlackTest() {
			this.slackTesting = true
			try {
				await restApi.post('/shortlink-settings/slack-test')
				this.$swal.fire({ icon: 'success', title: 'Test message sent to Slack', timer: 2500, showConfirmButton: false })
			} catch (error) {
				this.showError(error)
			} finally {
				this.slackTesting = false
			}
		},
		async loadTests() {
			this.loadingTests = true
			try {
				const { data } = await restApi.get('/shortlink-settings/tests')
				this.tests = data
			} catch (error) {
				this.showError(error)
			} finally {
				this.loadingTests = false
			}
		},
		async createTest() {
			this.creatingTest = true
			try {
				const { data } = await restApi.post('/shortlink-settings/tests', this.test)
				this.tests = [data, ...this.tests]
				this.test = { ...EMPTY_TEST, destination: this.test.destination, shortlink_domain: this.test.shortlink_domain }
			} catch (error) {
				this.showError(error)
			} finally {
				this.creatingTest = false
			}
		},
		async deleteTest(item) {
			try {
				await restApi.delete(`/shortlink-settings/tests/${item.id}`)
				this.tests = this.tests.filter(test => test.id !== item.id)
			} catch (error) {
				this.showError(error)
			}
		},
		formatDate(value) {
			return formatUtc(value, 'MMM D, h:mm A')
		},
		async copy(value) {
			await navigator.clipboard.writeText(value)
			this.$swal.fire({ icon: 'success', title: 'Copied', timer: 1200, showConfirmButton: false })
		},
		showError(error) {
			const data = (error.response && error.response.data) || {}
			const first = data.error && typeof data.error === 'object' ? Object.values(data.error)[0] : null
			this.$swal.fire({ icon: 'error', title: data.message || (first && first[0]) || 'Something went wrong.', timer: 6000 })
		},
	},
}
</script>

<style scoped>
.sl-domain {
	display: flex;
	align-items: flex-end;
	gap: 12px;
	margin-bottom: 12px;
	padding-bottom: 12px;
	border-bottom: 1px solid #f1f3f5;
}

.sl-domain__grid {
	display: grid;
	flex: 1;
	grid-template-columns: 1fr 1.4fr 1.4fr;
	gap: 0 12px;
	min-width: 0;
}

.sl-domain__actions {
	display: flex;
	align-items: center;
	gap: 10px;
	padding-bottom: 6px;
}

.sl-domain__default {
	display: flex;
	align-items: center;
	gap: 4px;
	margin: 0;
	color: #596170;
	font-size: 12px;
	white-space: nowrap;
}

.sl-utm-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 0 12px;
}

.sl-tests-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}

.sl-test {
	display: flex;
	flex-wrap: wrap;
	gap: 14px;
	align-items: flex-start;
	padding: 14px 0;
	border-top: 1px solid #f1f3f5;
}

.sl-test:first-of-type {
	margin-top: 10px;
}

.sl-test__qr {
	flex-shrink: 0;
	width: 132px;
	height: 132px;
	border: 1px solid #e6e8ec;
	border-radius: 8px;
	overflow: hidden;
	background: #fff;
}

.sl-test__body {
	flex: 1;
	min-width: 0;
	font-size: 13px;
}

.sl-test__row {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	margin-bottom: 4px;
}

.sl-test__row--muted {
	color: #6b7280;
}

.sl-test__label {
	flex-shrink: 0;
	width: 64px;
	color: #8a909c;
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.sl-test__redirect {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 12px;
}

.sl-test__meta {
	margin-top: 6px;
	color: #596170;
	font-size: 12px;
}

.sl-test__actions {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 8px;
}

.sl-link-btn {
	margin-right: 8px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #315efb;
	font-size: 12px;
	font-weight: 600;
}

.sl-test__timeline {
	flex-basis: 100%;
	order: 3;
	padding: 10px 0 0 146px;
}

.sl-icon-btn {
	padding: 0 4px;
	border: 0;
	background: transparent;
	color: #adb5bd;
}

.sl-icon-btn:hover {
	color: #2b303b;
}

@media (max-width: 767px) {
	.sl-utm-grid,
	.sl-domain__grid {
		grid-template-columns: 1fr;
	}

	.sl-domain {
		flex-direction: column;
		align-items: stretch;
	}

	.sl-test__timeline {
		padding-left: 0;
	}
}

.sl-secret {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
}

.sl-secret__status {
	color: #b45309;
	font-size: 13px;
}

.sl-secret__status--ok {
	color: #15803d;
}

.sl-new-secret {
	margin-top: 12px;
	padding: 12px;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	background: #f7f8fa;
}

.sl-new-secret__head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 8px;
	font-size: 13px;
}

.sl-new-secret__value {
	display: block;
	word-break: break-all;
	font-size: 12px;
	color: #2b303b;
}
</style>
